import React from "react";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/styles";
import { withRouter } from "react-router-dom";
import Close from "@material-ui/icons/Close";
import PhotoCamera from "@material-ui/icons/PhotoCamera";
import { I18n } from "@aws-amplify/core";
import { get } from "lodash";

import CameraScanner from "../Common/CameraScanner";
import { parseScannedCode } from "../../utils/scanCode";
import Barcode from './assets/mdi_barcode_scan_black.svg';

const styles = {
  scanPopup: {
    width: "400px",
    minHeight: "230px",
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "center",
    fontSize: "14px",
    boxSizing: "border-box",
    '@media(max-width: 470px)': {
      width: "350px",
      padding: "16px 0",
    }
  },
  scanPopupNav: {
    width: "100%",
    height: "24px",
    display: "grid",
    gridTemplateColumns: "1fr 24px",
    placeItems: "center",
    marginBottom: "40px",
  },
  interactPanel: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-evenly",
    alignItems: "center",
    minHeight: 0,
    '@media(max-width: 470px)': {
      width: "100%",
      padding: "0 4px"
    }
  },
  compactSpacing: {
    '@media(max-width: 470px)': {
      "& $scanPopupNav": {
        marginBottom: "16px",
      },
      "& $buttonContainer": {
        marginTop: "16px",
      },
    }
  },
  scanTitle: {
    display: "flex",
    alignItems: "center",
    "& div": {
      marginLeft: "8px",
    }
  },
  closeButton: {
    justifySelf: "end",
    alignSelf: "center",
    width: "24px",
    height: "24px",
    marginRight: "15px",
    "&:hover": {
      backgroundColor: "#f2f9fa",
      borderRadius: "2px",
      cursor: "pointer",
    }
  },
  input: {
    width: "352px",
    height: "44px",
    borderRadius: "2px",
    border: "1px solid #00000066",
    paddingLeft: "8px",
    marginTop: "8px",
    '@media(max-width: 470px)': {
      width: "100%",
    }
  },
  errorMsg: {
    color: "#eb3c39",
    fontSize: "14px",
  },
  buttonContainer: {
    marginTop: "40px",
    display: "flex",
    justifyContent: "space-between",
  },
  confirmBtn: {
    width: "122px",
    height: "36px",
    backgroundColor: "#5f00a0",
    borderRadius: "2px",
    border: "none",
    color: "white",
    marginLeft: "24px",
    fontWeight: "500",
    boxShadow: "0px 2px 2px #00000033",
  },
  cancelBtn: {
    width: "122px",
    height: "36px",
    backgroundColor: "white",
    borderRadius: "2px",
    border: "none",
    color: "#5f00a0",
    fontWeight: "500",
  },
  cameraToggle: {
    marginTop: "16px",
    minHeight: "36px",
    padding: "0 12px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "white",
    borderRadius: "2px",
    border: "1px solid #5f00a0",
    color: "#5f00a0",
    fontWeight: "500",
    fontSize: "14px",
    cursor: "pointer",
  },
  cameraPanel: {
    width: "352px",
    marginTop: "16px",
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    '@media(max-width: 470px)': {
      width: "100%",
    }
  },
};

const ScanPopup = (props) => {
  const { classes } = props;

  const [value, setValue] = React.useState("");
  const [cameraOpen, setCameraOpen] = React.useState(false);

  /**
   * Route un payload de scan vers l'écran appelant.
   * @returns {boolean} false si le payload n'a pas pu être interprété
   */
  const submitCode = (raw, options) => {
    const scanId = parseScannedCode(raw, options);
    if (scanId === null) {
      return false;
    }
    props.handleScanDialog(scanId);
    return true;
  }

  const handleSetValue = e => {
    const raw = e.target.value;
    props.setScanError("")
    setValue(raw)

    if (raw.trim().startsWith("{")) {
      submitCode(raw)
    }
  }

  const handleKeyDown = e => {
    if (e.key === "Enter" && value) {
      submitCode(value, { minDigits: 6 })
    }
  }

  const handleCameraDecoded = decodedText => {
    props.setScanError("")

    const scanId = parseScannedCode(decodedText);
    if (scanId === null) {
      console.warn(`Unrecognized QR payload: ${decodedText}`);
      props.setScanError(I18n.get("qrScanFormat"))
      return;
    }

    setCameraOpen(false)
    props.handleScanDialog(scanId)
  }

  return (
    <div className={cameraOpen ? `${classes.scanPopup} ${classes.compactSpacing}` : classes.scanPopup}>
      <div className={classes.scanPopupNav}>
        <div className={classes.scanTitle}>
          <img src={Barcode} alt="Scan Product" />
          <div>{I18n.get("ready_scan").toUpperCase()}</div>
        </div>
        <div className={classes.closeButton}>
          <Close onClick={() => { props.setOpenScan(false); props.setScanError("") }} />
        </div>
      </div>
      <div className={classes.interactPanel}>
        <input
          className={classes.input}
          id="user_input"
          onKeyDown={handleKeyDown}
          onInput={e => handleSetValue(e)}
          autoFocus
        >
        </input>
        <button
          className={classes.cameraToggle}
          type="button"
          aria-expanded={cameraOpen}
          onClick={() => { props.setScanError(""); setCameraOpen(!cameraOpen) }}
        >
          <PhotoCamera fontSize="small" />
          <span>{cameraOpen ? I18n.get("qrScanCameraStop") : I18n.get("qrScanCameraStart")}</span>
        </button>
        {cameraOpen && (
          <div className={classes.cameraPanel}>
            <CameraScanner
              onDecoded={handleCameraDecoded}
              onError={message => props.setScanError(message)}
            />
          </div>
        )}
        {props.errorMsg ? <div className={classes.errorMsg}>{props.errorMsg}</div> : null}
      </div>
      <div className={classes.buttonContainer}>
        <button
          className={classes.cancelBtn}
          type="button"
          onClick={() => { props.setOpenScan(false); props.setScanError("") }}>{I18n.get("site_cancel").toUpperCase()}</button>
        <button
          className={classes.confirmBtn}
          type="button"
          onClick={() => submitCode(value, { minDigits: 6 })}>{I18n.get("site_search").toUpperCase()}</button>
      </div>
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    language: get(state, ["user", "language"]),
  };
};

export default withRouter(
  connect(mapStateToProps)(withStyles(styles)(ScanPopup))
);